import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Search,
  Phone,
  PhoneForwarded,
  Users,
  History,
  BarChart3,
  Settings,
  Mic,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Download,
  Monitor
} from "lucide-react";

const isDesktopApp = !!(window.electronAPI);

const helpSections = [
  {
    id: "installation",
    title: "Installation & Setup",
    icon: Download,
    items: [
      {
        question: "What is the best way to use CONNECT?",
        answer: "For the best experience, we recommend installing the CONNECT Desktop App. It runs natively on your computer, starts automatically when you log in, and reliably receives incoming calls even when minimized. You can download it from your Profile Settings page."
      },
      {
        question: "How do I install the CONNECT Desktop App?",
        answer: "Go to your Profile Settings (click your name in the sidebar), then scroll to the 'Desktop Application' section. Click 'Download for Windows' to get the installer. Run the installer and follow the prompts. The app will start automatically when your computer boots up."
      },
      {
        question: "Can I use CONNECT in a web browser instead?",
        answer: "Yes, CONNECT works in modern browsers like Chrome, Edge, and Firefox. However, there are limitations: when the browser tab is minimized or in the background, you may miss incoming calls because browsers suspend inactive tabs. For reliable call reception, use the Desktop App or keep the CONNECT browser tab visible."
      },
      {
        question: "How do I enable notifications for incoming calls?",
        answer: "When prompted, click 'Allow' to enable browser notifications. This allows you to see a notification when a call comes in. However, note that if your browser tab is suspended (minimized), the call itself may not connect properly. The Desktop App doesn't have this limitation."
      }
    ]
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: Monitor,
    items: [
      {
        question: "I see a notification for an incoming call, but when I click it there's no call",
        answer: "This happens when using CONNECT in a web browser with the tab minimized. Browsers suspend inactive tabs to save resources, which disconnects the phone line. The notification arrives (via a separate system), but the actual call can't connect. Solution: Use the Desktop App, or keep the CONNECT browser tab visible/not minimized."
      },
      {
        question: "Why do I miss calls when my browser is minimized?",
        answer: "Modern browsers aggressively suspend background tabs to save battery and CPU. When suspended, CONNECT's phone connection is paused and can't receive calls. You'll still get a notification (if enabled), but by the time you click it, the call may have gone to voicemail. The Desktop App solves this problem."
      },
      {
        question: "The app says 'Connecting...' but never connects",
        answer: "Check your internet connection and try refreshing the page. If the problem persists, your network may be blocking WebSocket connections. Try a different network or contact your IT administrator. Some corporate firewalls block VoIP traffic."
      },
      {
        question: "I can't hear the other person / they can't hear me",
        answer: "Go to Profile Settings and check your microphone and speaker selections. Make sure the correct devices are selected. Also ensure your browser has permission to access your microphone - you may see a microphone icon in your browser's address bar that you can click to check permissions."
      },
      {
        question: "Calls are choppy or have poor quality",
        answer: "Call quality depends on your internet connection. For best results: use a wired connection instead of WiFi, close other bandwidth-heavy applications, and ensure you have at least 1 Mbps upload speed. If on WiFi, try moving closer to your router."
      }
    ]
  },
  {
    id: "getting-started",
    title: "Getting Started",
    icon: HelpCircle,
    items: [
      {
        question: "How do I sign in?",
        answer: "Use your SIP phone number and password provided by your administrator. If you're a new user, use the Register tab to create your account with your Cloud PBX credentials."
      },
      {
        question: "What is the difference between Sign In and Register?",
        answer: "Sign In is for returning users who already have an account. Register is for first-time users who need to create an account using their Cloud PBX credentials."
      },
      {
        question: "How do I change my theme?",
        answer: "Click on your profile name at the bottom of the sidebar, then select 'Light Mode' or 'Dark Mode' from the dropdown menu."
      }
    ]
  },
  {
    id: "making-calls",
    title: "Making & Receiving Calls",
    icon: Phone,
    items: [
      {
        question: "How do I make a call?",
        answer: "Navigate to 'My Phone' in the sidebar. Enter the phone number using the dial pad or type it directly, then click the green call button. You can also click on a contact to call them directly."
      },
      {
        question: "How do I answer an incoming call?",
        answer: "When a call comes in, you'll see a popup with the caller's information. Click the green phone button to answer or the red button to reject the call."
      },
      {
        question: "How do I end a call?",
        answer: "Click the red 'End Call' button on the dialer screen or on the floating call widget if you're on another page."
      },
      {
        question: "Can I make calls from other pages?",
        answer: "Yes! When you're on a call, a floating widget appears that lets you control the call from any page. You can mute, hold, or end the call without returning to the dialer."
      }
    ]
  },
  {
    id: "call-controls",
    title: "Call Controls",
    icon: Mic,
    items: [
      {
        question: "How do I mute my microphone?",
        answer: "During a call, click the 'MUTE' button. The button will turn red and show 'UNMUTE' when muted. Click again to unmute."
      },
      {
        question: "How do I put a caller on hold?",
        answer: "During a call, click the 'HOLD' button. The caller will hear hold music from your PBX. Click 'RESUME' to take them off hold."
      },
      {
        question: "Can I use the dial pad during a call?",
        answer: "Yes! The dial pad sends DTMF tones during a call, useful for navigating phone menus or entering PIN codes."
      }
    ]
  },
  {
    id: "transfers",
    title: "Call Transfers",
    icon: PhoneForwarded,
    items: [
      {
        question: "What is a Blind Transfer?",
        answer: "A blind transfer immediately connects the caller to the transfer target without you speaking to the target first. The call is transferred as soon as you select the destination."
      },
      {
        question: "What is an Attended Transfer?",
        answer: "An attended transfer lets you speak to the transfer target first before connecting the caller. This allows you to introduce the call or provide context. The original caller is placed on hold during this time."
      },
      {
        question: "How do I transfer a call?",
        answer: "During a call, click the 'TRANSFER' button. Search for or select an internal contact, then choose either 'Blind Transfer' or 'Attended Transfer'."
      },
      {
        question: "Can I cancel an attended transfer?",
        answer: "Yes! If you've started an attended transfer and want to go back to the original caller, click 'Cancel Transfer'. The original caller will be taken off hold."
      }
    ]
  },
  {
    id: "contacts",
    title: "Contacts",
    icon: Users,
    items: [
      {
        question: "Where do my contacts come from?",
        answer: "Contacts can come from two sources: internal contacts synced from your Cloud PBX, and external contacts you add manually."
      },
      {
        question: "How do I add a new contact?",
        answer: "Go to the Contacts page and click 'Add Contact'. Fill in the contact details and save."
      },
      {
        question: "How do I sync contacts from my PBX?",
        answer: "Contact syncing is configured by your administrator in the Company Settings. Once configured, internal contacts are automatically imported."
      },
      {
        question: "Can I mark contacts as favorites?",
        answer: "Yes! Click the star icon on any contact to mark them as a favorite. Favorite contacts appear at the top of your contact list."
      },
      {
        question: "What do the colored dots on contacts mean?",
        answer: "The colored dots show real-time presence status for internal contacts:\n\n• 🟢 Green = Logged In - The contact has the CONNECT softphone open\n• 🔴 Red = On a Call - The contact is currently busy on a call\n• ⚪ Gray = Offline - The contact is not currently using the softphone\n\nYou'll also see a 'Busy' badge when someone is on a call. Presence updates automatically every few seconds."
      },
      {
        question: "Why don't external contacts show presence?",
        answer: "Presence indicators only work for internal contacts (your team members) because they require the CONNECT softphone to report their status in real-time. External contacts are from outside your organization and don't use the CONNECT app."
      },
      {
        question: "How does the presence system work?",
        answer: "When you have the Dialer page open, CONNECT automatically reports your availability to the server. Your status shows as 'Logged In' (green) while the page is active. If you close the browser or navigate away, your status will change to 'Offline' (gray) after about 2 minutes. When you're on a call, it automatically shows as 'Busy' (red)."
      }
    ]
  },
  {
    id: "call-history",
    title: "Call History",
    icon: History,
    items: [
      {
        question: "What is 'My Calls'?",
        answer: "'My Calls' shows your personal call history - all calls you've made, received, or missed."
      },
      {
        question: "What is 'Call History' (Admin)?",
        answer: "The Call History page (available to Phone Admins) shows all calls across your organization, useful for monitoring and reporting."
      },
      {
        question: "Can I call back from the history?",
        answer: "Yes! Click on any call in your history to see details, and use the call button to call that number back."
      }
    ]
  },
  {
    id: "queue-monitor",
    title: "Queue Monitor",
    icon: BarChart3,
    items: [
      {
        question: "What does the Queue Monitor show?",
        answer: "The Queue Monitor displays all currently active calls in real-time, including caller information, call duration, and which agent is handling each call."
      },
      {
        question: "Who can see the Queue Monitor?",
        answer: "All users can see the Queue Monitor to view active calls in their organization."
      }
    ]
  },
  {
    id: "settings",
    title: "Settings & Preferences",
    icon: Settings,
    items: [
      {
        question: "How do I change my audio devices?",
        answer: "Go to Profile Settings from your user menu. You can select your preferred microphone and speaker from the available devices."
      },
      {
        question: "How do I update my profile?",
        answer: "Click your name at the bottom of the sidebar and select 'Profile Settings'. You can update your display name and upload a profile picture."
      },
      {
        question: "How do I restart the tour?",
        answer: "Click your name at the bottom of the sidebar and select 'Take a Tour' to restart the walkthrough at any time."
      }
    ]
  },
  {
    id: "plans",
    title: "Plans & Subscriptions",
    icon: HelpCircle,
    items: [
      {
        question: "What plans are available?",
        answer: "CONNECT offers two plans: Free (up to 2 users with basic features) and Pro (unlimited users with full features including AI transcripts, analytics, and priority support)."
      },
      {
        question: "How do I upgrade my plan?",
        answer: "Click your name at the bottom of the sidebar and select 'Manage Subscription' to view available plans and upgrade options."
      },
      {
        question: "What features are included in the Free plan?",
        answer: "The Free plan includes WebRTC softphone, basic contact management, 30-day call history, and community support for up to 2 users."
      },
      {
        question: "What additional features does Pro include?",
        answer: "Pro includes unlimited users, unlimited call history, queue monitoring, analytics dashboard, AI transcripts & summaries, global contacts directory, and priority support."
      }
    ]
  }
];

export default function Help() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState(["getting-started"]);
  const [expandedItems, setExpandedItems] = useState({});
  const [desktopVersion, setDesktopVersion] = useState(null);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    if (isDesktopApp && window.electronAPI?.getVersion) {
      window.electronAPI.getVersion().then(v => setDesktopVersion(v)).catch(() => {});
    }
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const toggleItem = (sectionId, itemIndex) => {
    const key = `${sectionId}-${itemIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Filter sections and items based on search
  const filteredSections = helpSections.map(section => ({
    ...section,
    items: section.items.filter(item =>
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.items.length > 0 || section.title.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Help & Support</h1>
        <p className="text-muted-foreground">Find answers to common questions about using CONNECT</p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search for help..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Help Sections */}
      <div className="space-y-4">
        {filteredSections.map((section) => (
          <Card key={section.id}>
            <CardHeader 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => toggleSection(section.id)}
            >
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <section.icon className="w-4 h-4 text-primary" />
                  </div>
                  <span>{section.title}</span>
                  <Badge variant="secondary" className="ml-2">{section.items.length}</Badge>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
              </CardTitle>
            </CardHeader>
            
            {expandedSections.includes(section.id) && (
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {section.items.map((item, index) => {
                    const isExpanded = expandedItems[`${section.id}-${index}`];
                    return (
                      <div 
                        key={index}
                        className="border rounded-lg overflow-hidden"
                      >
                        <button
                          className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
                          onClick={() => toggleItem(section.id, index)}
                        >
                          <span className="font-medium">{item.question}</span>
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-3 text-muted-foreground">
                            <p className="whitespace-pre-line">{item.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* No results */}
      {filteredSections.length === 0 && (
        <div className="text-center py-12">
          <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No results found</h3>
          <p className="text-muted-foreground">Try searching with different keywords</p>
        </div>
      )}

      {/* Contact Support */}
      <Card className="mt-8">
        <CardContent className="py-6">
          <div className="text-center">
            <h3 className="font-semibold mb-2">Still need help?</h3>
            <p className="text-muted-foreground mb-4">
              Contact your administrator or visit our support page for additional assistance.
            </p>
            <Link to="/app/about" className="text-primary hover:underline">
              View Contact Information →
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Version Info */}
      <div className="mt-6 text-center text-xs text-muted-foreground" data-testid="version-info">
        {desktopVersion && (
          <p><Monitor className="w-3 h-3 inline mr-1" />Desktop App v{desktopVersion}</p>
        )}
      </div>
    </div>
  );
}
